import { DateTime } from "luxon";

import { getFirstStartedAtTs } from "@intelligems/ig-analytics";
import {
  BaseTinybirdMetricsType,
  ExperienceFe,
  VariationFe,
} from "@intelligems/ig-types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DashboardPageName } from "@src/api/types/dashboards";
import { CHECKOUT_BLOCK_VIEWED_AVAILABLE_FROM } from "@src/features/analytics/constants";

import {
  getDefaultCompareGroupTb,
  getDefaultHiddenEndTime,
  getDefaultPersonalizationStartTime,
  getInitialControl,
} from "../helpers";
import { getFiltersFromUrl } from "../middleware/dashboardFiltersUrlHelpers";
import type { IExperienceDashboardFilters } from "../types/dashboardFilters";
import { initialDashboardFiltersState } from "../types/dashboardFilters";

const slice = createSlice({
  name: "dashboardFilters",
  initialState: initialDashboardFiltersState,
  reducers: {
    clearDashboardFilters: (draft) => {
      Object.assign(draft, initialDashboardFiltersState);
    },
    setDashboardFilter: (
      draft,
      action: PayloadAction<IExperienceDashboardFilters>
    ) => {
      Object.assign(draft, action.payload);
    },
    refreshData: (draft) => {
      if (draft.endTime !== null) {
        draft.hiddenEndTime = draft.endTime as number;
      } else {
        draft.hiddenEndTime = DateTime.now().toMillis();
      }
      return draft;
    },
    setDashboardFilterByExperience: (
      draft,
      action: PayloadAction<{
        experience: ExperienceFe;
        variations: VariationFe[];
        dashboardData: BaseTinybirdMetricsType[] | undefined;
        maxZScore: string;
        primaryMetricId: string;
        timezone?: string;
        everyVariationHasCheckoutBlock?: boolean;
        everyVariationHasPostPurchase?: boolean;
      }>
    ) => {
      const urlFilters = getFiltersFromUrl();
      const {
        experience,
        dashboardData,
        maxZScore,
        variations,
        primaryMetricId,
        timezone,
        everyVariationHasCheckoutBlock = false,
        everyVariationHasPostPurchase = false,
      } = action.payload;

      const controlGroup = variations.find((group) => group.isControl);

      // Bounds of this experience's lifetime, mirroring the backend's
      // analytics date-range validation (analysis.controller.ts).
      const experienceEndTs = getDefaultHiddenEndTime(experience);
      const experienceStartTs =
        getFirstStartedAtTs(experience.experienceActions) ?? 0;

      // Default the "User Behavior" filter to the stage matching the test.
      // A conversion stage is a fair comparison only when *every* variation can
      // reach it, so callers pass per-variation every… flags (from the
      // checkoutBlocks slice). testTypes only says the test has checkout blocks,
      // not that every variation carries one, hence the caller-passes-flags
      // contract.
      // checkout_block_viewed events only started being collected on the
      // reference date. Block-view data exists for this test iff it was still
      // collecting on/after that date — i.e. its effective end (paused/ended ts,
      // or now for a running test) reaches the reference. A test that fully ended
      // before it has no block-view series, so fall back to "started"
      // (everyVariationHasCheckoutBlock implies hasTestCheckoutBlocks). A test
      // that straddles the date still defaults to the block stage; the
      // results-view warning flags the missing early slice.
      const blockViewDataAvailable =
        experienceEndTs >=
        DateTime.fromISO(CHECKOUT_BLOCK_VIEWED_AVAILABLE_FROM).toMillis();

      const defaultReachedCheckoutStage = everyVariationHasPostPurchase
        ? "post_purchase_offer_viewed"
        : everyVariationHasCheckoutBlock && blockViewDataAvailable
          ? "checkout_block_viewed"
          : experience.testTypes.hasTestCheckoutBlocks
            ? "started"
            : undefined;

      if (controlGroup) {
        draft = {
          ...initialDashboardFiltersState,

          ...(defaultReachedCheckoutStage && {
            reachedCheckoutStage: defaultReachedCheckoutStage,
          }),
          maxZScore,
          analyticsViewType: experience.experienceAnalysis.analyticsViewType,
          startTime:
            experience.category === "personalization"
              ? getDefaultPersonalizationStartTime(experience, timezone)
              : null,
          endTime:
            experience.category === "personalization" &&
            experience.status === "paused"
              ? experienceEndTs
              : null,
          hiddenEndTime: experienceEndTs,
          experienceId: experience.id,
          compareGroupA: getInitialControl(variations),
          ...(experience.category === "experiment" && {
            compareGroupB: getDefaultCompareGroupTb(
              variations,
              controlGroup,
              dashboardData as any[],
              primaryMetricId
            ),
          }),
        };
      }

      Object.keys(urlFilters).forEach((key) => {
        if (urlFilters[key] === undefined) {
          return;
        }
        // startTime/endTime are synced through the URL and persist across
        // experiment navigation. A range belonging to a previously viewed
        // experience can fall entirely outside this experience's lifetime,
        // which the analytics backend rejects with a 400. Skip stale bounds
        // so the experience's own defaults are used instead of issuing an
        // invalid query.
        if (
          key === "startTime" &&
          urlFilters.startTime != null &&
          Number(urlFilters.startTime) >= experienceEndTs
        ) {
          return;
        }
        if (
          key === "endTime" &&
          urlFilters.endTime != null &&
          Number(urlFilters.endTime) <= experienceStartTs
        ) {
          return;
        }
        draft[key] = urlFilters[key];
      });

      return draft;
    },
    setCompareGroups: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        dashboardData: BaseTinybirdMetricsType[] | undefined;
        primaryMetricId: string;
      }>
    ) => {
      const urlFilters = getFiltersFromUrl();
      const controlGroup = action.payload.variations.find(
        (group) => group.isControl
      );
      if (controlGroup) {
        // Only set compare groups if not already in URL
        if (!urlFilters.compareGroupA) {
          draft.compareGroupA = getInitialControl(action.payload.variations);
        }
        if (!urlFilters.compareGroupB) {
          draft.compareGroupB = getDefaultCompareGroupTb(
            action.payload.variations,
            controlGroup,
            action.payload.dashboardData as any[],
            action.payload.primaryMetricId
          );
        }
      }
    },
    updateDashboardFilter: (
      draft,
      action: PayloadAction<{
        key: keyof IExperienceDashboardFilters;
        value: any;
      }>
    ) => {
      Object.assign(draft, {
        ...draft,
        [action.payload.key]: action.payload.value,
      });
    },
    setAnalyticsSubpage: (
      draft,
      action: PayloadAction<{
        key: string;
        value: DashboardPageName;
      }>
    ) => {
      draft[action.payload.key] = action.payload.value;
      return draft;
    },
  },
  selectors: {
    getExperienceAnalyticsFilters: (state) => state,
    getExperienceAnalyticsPageName: (state) =>
      state.pageName as DashboardPageName,
  },
});

export const {
  clearDashboardFilters,
  updateDashboardFilter,
  setDashboardFilter,
  setDashboardFilterByExperience,
  setCompareGroups,
  setAnalyticsSubpage,
  refreshData,
} = slice.actions;
export const { getExperienceAnalyticsFilters, getExperienceAnalyticsPageName } =
  slice.selectors;

export const dashboardFiltersReducer = slice.reducer;
